import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Filter, RotateCcw, Edit, Trash2, Eye } from "lucide-react";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { getBestBadgeVariant, formatDate } from "@/lib/utils";
import { returnsAPI } from "@/lib/api";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { HubFilters } from "@/features/sales/hubFilters";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface Return {
	id: string;
	returnNumber: string;
	customerName: string;
	productName: string;
	quantity: number;
	amount: number;
	status: "pending" | "approved" | "rejected" | "completed";
	reason: string;
	returnDate: string;
	createdAt: string;
}

const ReturnsPage: React.FC<{ filters?: HubFilters }> = ({ filters }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { isRTL } = useLanguage();
	const { toast } = useToast();
	const currency = useCurrency();

	const [returns, setReturns] = useState<Return[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Pagination and filters
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalReturns, setTotalReturns] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedSupplier, setSelectedSupplier] = useState<string>("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// When embedded in the sales hub, the hub toolbar owns the filters
	useEffect(() => {
		if (!filters) return;
		setSearchTerm(filters.search);
		setSelectedStatus(filters.status);
		setDateFrom(filters.dateFrom);
		setDateTo(filters.dateTo);
		setCurrentPage(1);
	}, [filters]);

	useEffect(() => {
		fetchReturns();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, itemsPerPage, searchTerm, selectedSupplier, selectedStatus, dateFrom, dateTo]);

	const fetchReturns = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const response = await returnsAPI.getAll(currentPage, itemsPerPage, {
				search: searchTerm || undefined,
				supplierId: selectedSupplier || undefined,
				status: selectedStatus || undefined,
				startDate: dateFrom || undefined,
				endDate: dateTo || undefined,
			});

			// Standardized response structure: { returns, total, page, limit }
			const returnsData = response?.returns || [];
			setReturns(returnsData);
			setTotalReturns(response?.total || 0);
			setTotalPages(Math.ceil((response?.total || 0) / itemsPerPage));
			// Initialize itemsPerPage from backend response if available
			if (response?.limit && response.limit !== itemsPerPage) {
				setItemsPerPage(response.limit);
			}
		} catch (err) {
			setError(t("returns.errors.fetchFailed"));
			console.error("Returns fetch error:", err);
			toast({
				title: t("common.error"),
				description: t("returns.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeleteReturn = async (returnId: number | string) => {
		if (await confirmAction(t("returns.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await returnsAPI.delete(returnId);
				toast({
					title: t("common.success"),
					description: t("returns.messages.deleted"),
				});
				fetchReturns();
			} catch (err) {
				toast({
					title: t("common.error"),
					description: t("returns.errors.deleteFailed"),
					variant: "destructive",
				});
			}
		}
	};

	const getStatusBadge = (status: string) => {
		// Normalize status to lowercase for lookup
		const normalizedStatus = (status || "").toLowerCase();

		const statusConfig: Record<string, { variant: string; text: string }> = {
			pending: {
				variant: "warning",
				text: t("returns.status.pending"),
			},
			approved: {
				variant: "success",
				text: t("returns.status.approved"),
			},
			rejected: {
				variant: "danger",
				text: t("returns.status.rejected"),
			},
			processed: {
				variant: "success",
				text: t("returns.status.processed") || "Processed",
			},
			cancelled: {
				variant: "destructive",
				text: t("returns.status.cancelled") || "Cancelled",
			},
			completed: {
				variant: "success",
				text: t("returns.status.completed") || "Completed",
			},
		};

		const config = statusConfig[normalizedStatus] || {
			variant: "secondary",
			text: status || t("common.unknown"),
		};

		return <Badge variant={getBestBadgeVariant(config.variant)}>{config.text}</Badge>;
	};

	const getPaymentStatusBadge = (paymentStatus: string) => {
		// Normalize payment status to lowercase for lookup
		const normalizedStatus = (paymentStatus || "").toLowerCase();

		const paymentStatusConfig: Record<string, { variant: string; text: string }> = {
			pending: {
				variant: "warning",
				text: t("returns.paymentStatus.pending"),
			},
			unpaid: {
				variant: "destructive",
				text: t("returns.paymentStatus.unpaid"),
			},
			partial: {
				variant: "warning",
				text: t("returns.paymentStatus.partial"),
			},
			paid: {
				variant: "success",
				text: t("returns.paymentStatus.paid"),
			},
			completed: {
				variant: "success",
				text: t("returns.paymentStatus.completed"),
			},
			refunded: {
				variant: "secondary",
				text: t("returns.paymentStatus.refunded"),
			},
		};

		const config = paymentStatusConfig[normalizedStatus] || {
			variant: "secondary",
			text: paymentStatus || t("common.unknown"),
		};

		return <Badge variant={getBestBadgeVariant(config.variant)}>{config.text}</Badge>;
	};

	if (isLoading && currentPage === 1) {
		return <Loading />;
	}

	return (
		<div className={`space-y-6 ${isRTL ? "text-right" : "text-left"}`}>
			{/* Filters and Search */}
			{!filters && (
				<Card>
					<CardHeader className="flex flex-row items-center justify-between py-2">
						<CardTitle className="flex items-center gap-2 text-base">
							<Filter className="h-5 w-5" />
							{t("returns.searchAndFilters")}
						</CardTitle>
						<Button
							variant="link"
							className="text-destructive"
							onClick={() => {
								setSearchTerm("");
								setSelectedSupplier("");
								setSelectedStatus("");
								setDateFrom("");
								setDateTo("");
								setCurrentPage(1);
							}}
						>
							{t("returns.clearFilters")}
						</Button>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-5">
							<div className="relative">
								<Search
									className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground`}
								/>
								<Input
									placeholder={t("returns.searchPlaceholder")}
									value={searchTerm}
									onChange={e => setSearchTerm(e.target.value)}
									className={isRTL ? "pr-10" : "pl-10"}
								/>
							</div>

							<Input
								placeholder={t("returns.searchSupplier")}
								value={selectedSupplier}
								onChange={e => setSelectedSupplier(e.target.value)}
							/>

							<select
								value={selectedStatus}
								onChange={e => setSelectedStatus(e.target.value)}
								className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<option value="">{t("returns.allStatuses")}</option>
								<option value="pending">{t("returns.status.pending")}</option>
								<option value="approved">{t("returns.status.approved")}</option>
								<option value="rejected">{t("returns.status.rejected")}</option>
								<option value="completed">{t("returns.status.completed")}</option>
							</select>

							<Input
								type="date"
								placeholder={t("returns.fromDate")}
								value={dateFrom}
								onChange={e => setDateFrom(e.target.value)}
							/>

							<Input
								type="date"
								placeholder={t("returns.toDate")}
								value={dateTo}
								onChange={e => setDateTo(e.target.value)}
							/>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Returns Table */}
			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<div className="space-y-2">
						<CardTitle className="flex items-center gap-2 text-base">
							<RotateCcw className="w-6 h-6 shrink-0" />
							<span>{t("returns.title")}</span>
						</CardTitle>
						<CardDescription>{t("returns.description")}</CardDescription>
					</div>
					{!filters && (
						<PermissionGate permission="returns.create">
							<Button className="flex items-center gap-2" onClick={() => navigate("/returns/new")}>
								<Plus className="h-4 w-4" />
								{t("returns.newReturn")}
							</Button>
						</PermissionGate>
					)}
				</CardHeader>
				<CardContent>
					<div className="record-surface">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("returns.table.number")}</TableHead>
									<TableHead>{t("returns.table.supplierName")}</TableHead>
									<TableHead>{t("returns.table.code")}</TableHead>
									<TableHead className="text-center">{t("returns.table.numberOfProducts")}</TableHead>
									<TableHead className="text-center">{t("returns.table.status")}</TableHead>
									<TableHead className="text-center">{t("returns.table.payment")}</TableHead>
									<TableHead className="text-center">{t("returns.table.amount")}</TableHead>
									<TableHead className="text-center">{t("returns.table.paid")}</TableHead>
									<TableHead className="text-center">{t("returns.table.remaining")}</TableHead>
									<TableHead className="text-center">{t("returns.table.date")}</TableHead>
									<TableHead className="text-center">{t("returns.table.actions")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{!isLoading && returns.length === 0 && (
									<TableRow>
										<TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
											{t("common.noData")}
										</TableCell>
									</TableRow>
								)}
								{returns.map((returnItem: any) => (
									<TableRow key={returnItem.id}>
										<TableCell>{returnItem.id}</TableCell>
										<TableCell>
											{returnItem.supplier?.name || returnItem.supplierName || "-"}
										</TableCell>
										<TableCell className="font-medium">
											{returnItem.returnNumber || returnItem.code || "-"}
										</TableCell>
										<TableCell className="text-center">
											{returnItem.items?.length || returnItem.numberOfProducts || 0}
										</TableCell>
										<TableCell className="text-center">
											{getStatusBadge(returnItem.status)}
										</TableCell>
										<TableCell className="text-center">
											{returnItem.paymentStatus
												? getPaymentStatusBadge(returnItem.paymentStatus)
												: "-"}
										</TableCell>
										<TableCell className="text-center font-medium">
											<Price
												value={returnItem.totalAmount || returnItem.amount || 0}
												currency={currency}
											/>
										</TableCell>
										<TableCell className="text-center">
											<Price value={returnItem.paidAmount || 0} currency={currency} />
										</TableCell>
										<TableCell className="text-center">
											<Price
												value={
													(returnItem.totalAmount || returnItem.amount || 0) -
													(returnItem.paidAmount || 0)
												}
												currency={currency}
											/>
										</TableCell>
										<TableCell className="text-center text-muted-foreground">
											{formatDate(returnItem.createdAt || returnItem.returnDate)}
										</TableCell>
										<TableCell className="text-center">
											<div className="flex items-center justify-center gap-2">
												<PermissionGate permission="returns.read">
													<Button
														variant="outline"
														title={t("common.view")}
														aria-label={t("common.view")}
														size="icon"
														onClick={() => navigate(`/returns/${returnItem.id}`)}
													>
														<Eye className="h-4 w-4" />
													</Button>
												</PermissionGate>
												<PermissionGate permission="returns.update">
													<Button
														variant="outline"
														title={t("common.edit")}
														aria-label={t("common.edit")}
														size="icon"
														onClick={() => navigate(`/returns/${returnItem.id}/edit`)}
													>
														<Edit className="h-4 w-4 text-primary" />
													</Button>
												</PermissionGate>
												<PermissionGate permission="returns.delete">
													<Button
														variant="outline"
														title={t("common.delete")}
														aria-label={t("common.delete")}
														size="icon"
														onClick={() => handleDeleteReturn(returnItem.id)}
													>
														<Trash2 className="h-4 w-4 text-destructive" />
													</Button>
												</PermissionGate>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>

			{/* Pagination */}
			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalReturns}
				itemsPerPage={itemsPerPage}
				onPageChange={setCurrentPage}
				onPageSizeChange={setItemsPerPage}
				itemName="returns.returns"
			/>

			{/* Error State */}
			{error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="text-center">
							<RotateCcw className="h-12 w-12 text-destructive mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("returns.errorLoading")}</h3>
							<p className="text-muted-foreground mb-4">{error}</p>
							<Button onClick={fetchReturns}>{t("common.tryAgain")}</Button>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
};

export default ReturnsPage;
